/**
 * Cards Carousel: Splide init + Card Spotlight (CSS transitions) for 288-cards-carousel section.
 */
(function () {
  const SECTION_SELECTOR = '[id^="cards-carousel-"]';
  const SPLIDE_SELECTOR = ".cards-carousel__splide";

  function whenReady(fn) {
    if (document.readyState !== "loading") {
      fn();
    } else {
      document.addEventListener("DOMContentLoaded", fn);
    }
  }

  /**
   * Resolve the rendered pixel value of --ctx-rhythm in the section's context.
   * Splide cannot measure a `var()` gap (its internal probe element does not
   * inherit the section's custom properties), so it falls back to a different
   * value on every resize — on mobile the address bar show/hide fires a resize
   * mid-scroll, which recomputed the slide widths and made the side cards visibly
   * grow then shrink. Passing a concrete px value keeps the gap stable across
   * mount and resize. --ctx-rhythm is a fixed px token, so resolving once is enough.
   */
  function resolveGapPx(section) {
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:absolute;width:var(--ctx-rhythm, 1rem);height:0;visibility:hidden;pointer-events:none;";
    section.appendChild(probe);
    const px = probe.getBoundingClientRect().width;
    section.removeChild(probe);
    return px > 0 ? px : 16;
  }

  function initSection(section) {
    const splideEl = section.querySelector(SPLIDE_SELECTOR);
    if (!splideEl || typeof window.Splide === "undefined") return;

    const gapPx = resolveGapPx(section);

    const splide = new window.Splide(splideEl, {
      type: "loop",
      perPage: 4.5,
      perMove: 1,
      focus: "center",
      snap: 1,
      updateOnMove: true,
      gap: gapPx + "px",
      padding: { left: "0", right: "0" },
      arrows: false,
      flickPower: 200,
      flickMaxPages: 1,
      pagination: false,
      easing: "cubic-bezier(0.19, 1, 0.22, 1)",
      breakpoints: {
        1200: { perPage: 3.5 },
        990: { perPage: 2.5 },
        640: { perPage: 1.5 },
      },
      i18n: {
        prev: "Previous slide",
        next: "Next slide",
      },
    });

    if (section.dataset.spotlight === "true") {
      var lockPending = false;
      var lastTrackWidth = 0;
      var lockedHeight = 0;
      var heightFrozen = false;

      function measureMaxCardHeight() {
        section.dataset.spotlightMeasure = "true";

        var cards = splideEl.querySelectorAll(".cards-carousel__card");
        var maxHeight = 0;
        cards.forEach(function (card) {
          var height = card.offsetHeight;
          if (height > maxHeight) maxHeight = height;
        });

        delete section.dataset.spotlightMeasure;
        return maxHeight;
      }

      function applyTrackHeight(maxHeight) {
        if (maxHeight <= 0) return;
        lockedHeight = maxHeight;
        lastTrackWidth = splideEl.clientWidth;
        section.style.setProperty(
          "--cards-carousel-track-height",
          maxHeight + "px"
        );
        splideEl.style.height = maxHeight + "px";
        splideEl.style.minHeight = maxHeight + "px";
      }

      function lockTrackHeight(force) {
        if (heightFrozen && !force) return;

        var maxHeight = measureMaxCardHeight();
        if (maxHeight <= 0) return;

        if (force || Math.abs(maxHeight - lockedHeight) > 1) {
          applyTrackHeight(maxHeight);
        }
      }

      function markTrackHeightLocked() {
        section.dataset.trackHeightLocked = "true";
      }

      function markTrackHeightReady() {
        section.dataset.trackHeightReady = "true";
        heightFrozen = true;
      }

      function scheduleLockTrackHeight(force) {
        if (lockPending) return;
        lockPending = true;
        requestAnimationFrame(function () {
          lockPending = false;
          lockTrackHeight(force);
        });
      }

      function waitForStableLayout() {
        var imgPromises = [];
        splideEl.querySelectorAll("img").forEach(function (img) {
          if (img.complete) return;
          imgPromises.push(
            new Promise(function (resolve) {
              img.addEventListener("load", resolve, { once: true });
              img.addEventListener("error", resolve, { once: true });
            })
          );
        });

        var fontsReady =
          document.fonts && document.fonts.ready
            ? document.fonts.ready
            : Promise.resolve();

        return Promise.race([
          Promise.all(imgPromises.concat([fontsReady])),
          new Promise(function (resolve) {
            window.setTimeout(resolve, 2000);
          }),
        ]);
      }

      function finalizeTrackHeight() {
        lockTrackHeight(true);
        markTrackHeightLocked();

        if (section.dataset.appearAnimation !== "true") {
          markTrackHeightReady();
        }
      }

      // Rough lock before mount so the track is not `auto` on first paint.
      var preMountHeight = measureMaxCardHeight();
      if (preMountHeight > 0) {
        applyTrackHeight(preMountHeight);
      }

      splide.on("mounted", function () {
        lockTrackHeight(true);
        waitForStableLayout().then(function () {
          finalizeTrackHeight();
        });
      });

      splide.on("resize", function () {
        // On mobile, scrolling the page shows/hides the address bar, which fires
        // a window resize with only a HEIGHT change. Re-locking the track height
        // then re-measures every card (briefly opening all spotlights) and made
        // the card heights visibly jump on every scroll. Card content height only
        // depends on WIDTH, so re-lock only when the track width actually changed
        // (orientation change, real resize) — matching the ResizeObserver gate below.
        if (splideEl.clientWidth !== lastTrackWidth) {
          scheduleLockTrackHeight(true);
        }
      });

      if (typeof ResizeObserver !== "undefined") {
        var ro = new ResizeObserver(function () {
          if (splideEl.clientWidth !== lastTrackWidth) {
            scheduleLockTrackHeight(true);
          }
        });
        ro.observe(splideEl);
      }

      section.addEventListener("cards-carousel:appear-complete", function () {
        if (section.dataset.trackHeightReady === "true") return;
        markTrackHeightReady();
      });
    }

    splide.mount();
  }

  function run() {
    const sections = document.querySelectorAll(SECTION_SELECTOR);
    sections.forEach(initSection);
  }

  whenReady(run);

  document.addEventListener("shopify:section:load", function (e) {
    const section =
      e.target.querySelector(SECTION_SELECTOR) ||
      (e.target.matches(SECTION_SELECTOR) ? e.target : null);
    if (section) initSection(section);
  });
})();
